/**
 * Live bar aggregation (SPEC §12.2A bar types, §12.3 rules).
 *
 * This is the TypeScript stand-in for `helm_core::BarBuilder` until the Rust/WASM build lands; the
 * rules and the columnar layout are the contract the WASM version must keep.
 *
 * - Prices are integer ticks. Columns store them as Int32 **relative to `origin`** (the first
 *   price the series ever saw), so NQ at 85 800 ticks and a 1M-bar series both fit and the GPU can
 *   upload them as f32 without precision loss (§12.1 "moving origin").
 * - **Time bars** (under a day) count whole periods from the trading day's **17:00 CT open**
 *   (`sessionBucket`; owner decision 2026-09-27, Q-T3 / ADR-0015, replacing §12.3's UTC
 *   alignment): a 4h bar opens at 17:00, 21:00, 01:00 … CT in CST and CDT alike; periods that
 *   divide an hour keep the usual clock marks; odd periods (7m, 90s, 5h) restart at each session
 *   open and the session's last bar is cut at the next open. Empty periods produce no bar.
 *   Timestamps are integer **milliseconds** (exchange time); every period is whole seconds, so
 *   bucketing on ms is exact and keeps bigint out of the per-trade hot path.
 * - **Session bars** follow the CME trading day (calendar.ts): a daily bar is one trading day
 *   (17:00 CT → 16:00 CT, named by its close date), a weekly bar the Monday–Friday trading days
 *   (Sunday open → Friday close), a monthly bar the trading days whose close date falls in the
 *   calendar month (N-month bars group months from January). N-day bars group N consecutive
 *   trading days and N-week bars N weeks, both counted from the epoch Monday 1970-01-05
 *   (`tradingDayIndex`, `weekIndex`; Q-T4), so bars never move as history scrolls. `openTs` =
 *   the first trading day's 17:00 CT open.
 * - **Trade-built bars** (tick, volume, range, Renko) reset at the session open (§12.3 tick-bar
 *   default; NinjaTrader "break at EOD"): the first trade of a new trading day always opens a new
 *   bar and Renko re-anchors. Their `openTs` is the opening trade's time, bumped by 1 ms when a
 *   bar would otherwise share the previous bar's open time (several bars can open on one trade),
 *   so open times stay strictly increasing — the bar-index → time map and the history seam need
 *   that. The bump is deterministic, so a rebuild from the same trades gives the same bars.
 *   - Tick: close after N trades (one `TradeBatch` entry = one trade).
 *   - Volume: close when the bar holds exactly N contracts; a trade crossing the boundary is split
 *     and the remainder opens the next bar(s) at the same price and time (one large trade can
 *     fill several bars). Each bar a trade touches counts it once in `trades`.
 *   - Range (NinjaTrader semantics, phantom bars on): `high − low` never exceeds R ticks. A trade
 *     that would extend it closes the bar at its boundary (low + R, or high − R) and the next bar
 *     opens one tick beyond; a jump further than that fills **phantom bars** (exactly R ticks,
 *     no volume, no trades) so no price level is skipped, and the trade lands in the last one.
 *     With phantom bars **off** (`phantom: false`, a per-chart toggle, Q-T5) a jump that would
 *     need them opens the next bar at the trade's own price instead, leaving the gap visible.
 *   - Renko (classic, brick B): a brick completes when price reaches B beyond the last brick's
 *     close in the trend direction, or 2B against it (the reversal brick opens at the last
 *     brick's open). Bricks sit at exact multiples of B from the session's anchor (the first
 *     trade rounded to the nearest multiple of B); completed bricks have no wicks. The last bar is
 *     the **forming** brick: open = the last brick's close (or the anchor), close = last price,
 *     high/low = extremes since it started. Its volume moves into the brick it completes; extra
 *     bricks from one jump carry no volume.
 * - A late print (older than the current time/session bar) folds into the current bar rather than
 *   rewriting history; bars never move backwards. Trade-built bars treat it as the next trade.
 *
 * History (SPEC §6.8, SPEC-ADDENDA A1): `loadHistory(bars)` replaces the series with history bars
 * and re-derives the builder's state from the last bars (current bucket, trade count, volume,
 * range, Renko brick), so live trades continue the last history bar exactly as if they had been
 * fed after it. The chart replays its buffered live trades newer than the history's cutoff on top
 * (ChartView), which keeps the seam exact for every bar type. `BarSeries.replaceHistory(bars,
 * cutoffTs)` keeps its older contract (history prefix + live bars at/after `cutoffTs`).
 */
import {
  EPOCH_MONDAY,
  TradingDayCache,
  firstTradingDayOfMonth,
  lastTradingDayOfMonth,
  monthOf,
  tradingDayAt,
  tradingDayCloseMs,
  tradingDayIndex,
  tradingDayOf,
  tradingDayOpenMs,
  weekIndex,
} from "./calendar.ts";
import { specError } from "./timeframe.ts";

export type SessionUnit = "day" | "week" | "month";

export type BarSpec =
  /** Intraday time bars from the 17:00 CT session open; `periodMs` whole seconds, 1 s – 12 h. */
  | { kind: "time"; periodMs: number }
  /** CME trading-day bars: N days, N weeks, or 1/2/3/4/6/12 months. */
  | { kind: "session"; unit: SessionUnit; count: number }
  /** N trades per bar. */
  | { kind: "tick"; count: number }
  /** N contracts per bar. */
  | { kind: "volume"; count: number }
  /**
   * Range bars: high − low ≤ `ticks`. `phantom` (default true) fills price gaps with phantom
   * bars; false opens the next bar at the gapping trade's price.
   */
  | { kind: "range"; ticks: number; phantom?: boolean }
  /** Classic Renko, brick = `ticks`. */
  | { kind: "renko"; ticks: number };

/** Result of feeding one trade. */
export type BarChange = "updated" | "new";

/** A plain bar record, used for prepending history and for tests. Prices in absolute ticks. */
export type BarRecord = {
  openTs: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  buyVolume: number;
  sellVolume: number;
  trades: number;
  /**
   * Highest / lowest running aggressor delta (buy − sell volume, from 0 at the bar's open) reached
   * inside the bar, trade by trade (CVD candles' wicks, owner Q-S2). Present on bars built from
   * trades (live, tick/volume/range/Renko history from the session-trade cache); absent on
   * exchange history bars, which carry only the bar's totals.
   */
  deltaHigh?: number;
  deltaLow?: number;
};

/**
 * Running-delta extremes of bar `b` (`deltaHigh` / `deltaLow`); when it has none, the only points
 * known are its open (0) and its close (buy − sell).
 */
export function deltaExtremes(b: BarRecord): { hi: number; lo: number } {
  const d = b.buyVolume - b.sellVolume;
  return {
    hi: b.deltaHigh ?? Math.max(0, d),
    lo: b.deltaLow ?? Math.min(0, d),
  };
}

/**
 * `a` then `b` merged into one bar's running-delta extremes (history aggregation: 1m → 5m, the
 * session tail). Extremes a constituent does not carry come from its open and close, so an
 * aggregated exchange bar's wicks are exact to the constituent bars' resolution.
 */
export function mergeDeltaExtremes(a: BarRecord, b: BarRecord): { deltaHigh: number; deltaLow: number } {
  const ea = deltaExtremes(a);
  const eb = deltaExtremes(b);
  const da = a.buyVolume - a.sellVolume;
  return { deltaHigh: Math.max(ea.hi, da + eb.hi), deltaLow: Math.min(ea.lo, da + eb.lo) };
}

const INITIAL_CAPACITY = 1024;
/** SPEC §12.1 design ceiling per chart. When exceeded, the oldest quarter is dropped. */
export const MAX_BARS = 1_000_000;

export const AGGRESSOR_BUY = 1;
export const AGGRESSOR_SELL = 2;

/** Columnar bar storage (SPEC §12.1 "series live in columnar typed arrays"). */
export class BarSeries {
  length = 0;
  /** Absolute tick price that relative columns are measured from; null until the first trade. */
  origin: number | null = null;
  open: Int32Array;
  high: Int32Array;
  low: Int32Array;
  close: Int32Array;
  volume: Float64Array;
  buyVolume: Float64Array;
  sellVolume: Float64Array;
  trades: Uint32Array;
  /** Bar open time, epoch ms (the bar-index → time map, `realTs[]` in §12.1). */
  openTs: Float64Array;
  /** Running-delta extremes inside the bar (`BarRecord.deltaHigh/Low`); NaN = not known. */
  deltaHigh: Float64Array;
  deltaLow: Float64Array;
  /** Bumped whenever indices shift (prepend or trim) so views can re-upload everything. */
  generation = 0;
  /** Leading bars that came from history (the rest were built from live trades). */
  historyLength = 0;
  private readonly maxBars: number;

  constructor(capacity = INITIAL_CAPACITY, maxBars = MAX_BARS) {
    this.maxBars = maxBars;
    this.open = new Int32Array(capacity);
    this.high = new Int32Array(capacity);
    this.low = new Int32Array(capacity);
    this.close = new Int32Array(capacity);
    this.volume = new Float64Array(capacity);
    this.buyVolume = new Float64Array(capacity);
    this.sellVolume = new Float64Array(capacity);
    this.trades = new Uint32Array(capacity);
    this.openTs = new Float64Array(capacity);
    this.deltaHigh = new Float64Array(capacity);
    this.deltaLow = new Float64Array(capacity);
  }

  get capacity(): number {
    return this.open.length;
  }

  /** Absolute tick prices of bar `i`. */
  bar(i: number): BarRecord {
    const o = this.origin ?? 0;
    const r: BarRecord = {
      openTs: this.openTs[i] ?? 0,
      open: (this.open[i] ?? 0) + o,
      high: (this.high[i] ?? 0) + o,
      low: (this.low[i] ?? 0) + o,
      close: (this.close[i] ?? 0) + o,
      volume: this.volume[i] ?? 0,
      buyVolume: this.buyVolume[i] ?? 0,
      sellVolume: this.sellVolume[i] ?? 0,
      trades: this.trades[i] ?? 0,
    };
    const dh = this.deltaHigh[i] ?? Number.NaN;
    const dl = this.deltaLow[i] ?? Number.NaN;
    if (!Number.isNaN(dh) && !Number.isNaN(dl)) {
      r.deltaHigh = dh;
      r.deltaLow = dl;
    }
    return r;
  }

  clear(): void {
    this.length = 0;
    this.historyLength = 0;
    this.origin = null;
    this.generation++;
  }

  /** Append an empty slot and return its index (grows or trims storage as needed). */
  appendSlot(): number {
    if (this.length >= this.maxBars) this.trimOldest(Math.max(1, Math.floor(this.maxBars / 4)));
    if (this.length >= this.capacity) this.resize(Math.min(this.capacity * 2, Math.max(this.maxBars, 1)));
    return this.length++;
  }

  /**
   * Insert older bars before index 0 (history backfill). `bars` must be in time order and older
   * than the current first bar. Returns the index shift applied to existing bars.
   */
  prepend(bars: readonly BarRecord[]): number {
    const n = bars.length;
    if (n === 0) return 0;
    const first = bars[0];
    if (this.origin === null && first) this.origin = first.open;
    const origin = this.origin ?? 0;
    const need = this.length + n;
    if (need > this.capacity) this.resize(Math.max(need, this.capacity * 2));
    for (const col of this.columns()) col.copyWithin(n, 0, this.length);
    for (let i = 0; i < n; i++) {
      const b = bars[i];
      if (!b) continue;
      this.open[i] = b.open - origin;
      this.high[i] = b.high - origin;
      this.low[i] = b.low - origin;
      this.close[i] = b.close - origin;
      this.volume[i] = b.volume;
      this.buyVolume[i] = b.buyVolume;
      this.sellVolume[i] = b.sellVolume;
      this.trades[i] = b.trades;
      this.openTs[i] = b.openTs;
      this.deltaHigh[i] = b.deltaHigh ?? Number.NaN;
      this.deltaLow[i] = b.deltaLow ?? Number.NaN;
    }
    this.length = need;
    this.historyLength += n;
    this.generation++;
    return n;
  }

  /**
   * Replace the history prefix with `bars` (time order; prices absolute ticks) and drop live bars
   * that open before `cutoffTs`. Bars are de-duplicated on the way in: a history bar must open
   * strictly after the previous one and strictly before both `cutoffTs` and the first kept live
   * bar, so overlapping or unordered input can never produce two bars for one period.
   *
   * Returns the index shift of the kept live bars (new index − old index); when no live bar is
   * kept it is the change in length.
   */
  replaceHistory(bars: readonly BarRecord[], cutoffTs: number): number {
    const oldLength = this.length;
    let keepFrom = this.historyLength;
    while (keepFrom < this.length && (this.openTs[keepFrom] ?? 0) < cutoffTs) keepFrom++;
    const kept = this.length - keepFrom;
    const limit = kept > 0 ? Math.min(cutoffTs, this.openTs[keepFrom] ?? cutoffTs) : cutoffTs;
    const accepted: BarRecord[] = [];
    for (const b of inTimeOrder(bars)) {
      if (!(b.openTs < limit)) break;
      accepted.push(b);
    }
    // Stay under the bar ceiling by dropping the oldest history bars.
    const room = Math.max(0, this.maxBars - kept);
    const hist = accepted.length > room ? accepted.slice(accepted.length - room) : accepted;
    const n = hist.length;
    const first = hist[0];
    if (this.origin === null && first) this.origin = first.open;
    const origin = this.origin ?? 0;
    const need = n + kept;
    if (need > this.capacity)
      this.resize(Math.min(Math.max(need, this.capacity * 2), Math.max(this.maxBars, need)));
    if (kept > 0 && keepFrom !== n)
      for (const col of this.columns()) col.copyWithin(n, keepFrom, keepFrom + kept);
    for (let i = 0; i < n; i++) {
      const b = hist[i];
      if (!b) continue;
      this.open[i] = b.open - origin;
      this.high[i] = b.high - origin;
      this.low[i] = b.low - origin;
      this.close[i] = b.close - origin;
      this.volume[i] = b.volume;
      this.buyVolume[i] = b.buyVolume;
      this.sellVolume[i] = b.sellVolume;
      this.trades[i] = b.trades;
      this.openTs[i] = b.openTs;
      this.deltaHigh[i] = b.deltaHigh ?? Number.NaN;
      this.deltaLow[i] = b.deltaLow ?? Number.NaN;
    }
    this.length = need;
    this.historyLength = n;
    if (n === 0 && kept === 0) this.origin = null;
    this.generation++;
    return kept > 0 ? n - keepFrom : need - oldLength;
  }

  private columns(): (Int32Array | Float64Array | Uint32Array)[] {
    return [
      this.open,
      this.high,
      this.low,
      this.close,
      this.volume,
      this.buyVolume,
      this.sellVolume,
      this.trades,
      this.openTs,
      this.deltaHigh,
      this.deltaLow,
    ];
  }

  private trimOldest(k: number): void {
    for (const col of this.columns()) col.copyWithin(0, k, this.length);
    this.length -= k;
    this.historyLength = Math.max(0, this.historyLength - k);
    this.generation++;
  }

  private resize(cap: number): void {
    const grow = <T extends Int32Array | Float64Array | Uint32Array>(a: T, make: (n: number) => T): T => {
      const b = make(cap);
      b.set(a.subarray(0, Math.min(this.length, cap)));
      return b;
    };
    this.open = grow(this.open, (n) => new Int32Array(n));
    this.high = grow(this.high, (n) => new Int32Array(n));
    this.low = grow(this.low, (n) => new Int32Array(n));
    this.close = grow(this.close, (n) => new Int32Array(n));
    this.volume = grow(this.volume, (n) => new Float64Array(n));
    this.buyVolume = grow(this.buyVolume, (n) => new Float64Array(n));
    this.sellVolume = grow(this.sellVolume, (n) => new Float64Array(n));
    this.trades = grow(this.trades, (n) => new Uint32Array(n));
    this.openTs = grow(this.openTs, (n) => new Float64Array(n));
    this.deltaHigh = grow(this.deltaHigh, (n) => new Float64Array(n));
    this.deltaLow = grow(this.deltaLow, (n) => new Float64Array(n));
  }
}

/**
 * `bars` strictly increasing by open time: returned as is when already so (the common case),
 * otherwise sorted (stable) with the **last** record kept for a repeated open time.
 */
export function inTimeOrder(bars: readonly BarRecord[]): readonly BarRecord[] {
  let ordered = true;
  for (let i = 1; i < bars.length && ordered; i++) {
    ordered = (bars[i]?.openTs ?? 0) > (bars[i - 1]?.openTs ?? 0);
  }
  if (ordered) return bars;
  const sorted = bars.map((b, k) => ({ b, k })).sort((p, q) => p.b.openTs - q.b.openTs || p.k - q.k);
  const out: BarRecord[] = [];
  for (const { b } of sorted) {
    if (out.length > 0 && out[out.length - 1]?.openTs === b.openTs) out[out.length - 1] = b;
    else out.push(b);
  }
  return out;
}

/**
 * Session-bar bucket key of a trading day: N-day groups of trading days and N-week groups of weeks
 * counted from `EPOCH_MONDAY`, N-month groups of months from January 1970.
 */
export function sessionKey(unit: SessionUnit, count: number, day: number): number {
  if (unit === "day") return Math.floor(tradingDayIndex(day) / count);
  if (unit === "week") return Math.floor(weekIndex(day) / count);
  return Math.floor(monthOf(day) / count);
}

/** First trading day (day id) of a session-bar bucket. */
function sessionFirstDay(unit: SessionUnit, count: number, key: number): number {
  if (unit === "day") return tradingDayAt(key * count);
  if (unit === "week") return EPOCH_MONDAY + key * count * 7;
  return firstTradingDayOfMonth(key * count);
}

/** Open (UTC ms, 17:00 CT before its first trading day) of a session-bar bucket. */
export function sessionOpenMs(unit: SessionUnit, count: number, key: number): number {
  return tradingDayOpenMs(sessionFirstDay(unit, count, key));
}

/** Close (UTC ms, 16:00 CT of the bucket's last trading day) of a session-bar bucket. */
function sessionCloseMs(unit: SessionUnit, count: number, key: number): number {
  if (unit === "day") return tradingDayCloseMs(tradingDayAt(key * count + count - 1));
  if (unit === "week") return tradingDayCloseMs(EPOCH_MONDAY + (key * count + count - 1) * 7 + 4);
  return tradingDayCloseMs(lastTradingDayOfMonth(key * count + count - 1));
}

export class BarBuilder {
  readonly spec: BarSpec;
  readonly series: BarSeries;
  /** Bars appended by the last `push` (range, volume and Renko can add several per trade). */
  lastAdded = 0;
  /** Bucket key of the current (last) time/session bar (a time bar's key is its open); NaN when none. */
  private bucket = Number.NaN;
  /** Time bars: exclusive end of the current bucket (cut at the session reset). */
  private bucketEnd = Number.NaN;
  /** End of the bucket last computed by `bucketKey` (time bars). */
  private keyEnd = Number.NaN;
  /** Trade-built bars: trading day of the current bar; NaN when none. */
  private session = Number.NaN;
  private readonly days = new TradingDayCache();
  /** Renko: last completed brick of the session (NaN = none yet). */
  private brickOpen = Number.NaN;
  private brickClose = Number.NaN;

  constructor(spec: BarSpec, series: BarSeries = new BarSeries()) {
    const err = specError(spec);
    if (err !== null) throw new Error(`invalid bar spec ${JSON.stringify(spec)}: ${err}`);
    this.spec = spec;
    this.series = series;
  }

  reset(): void {
    this.series.clear();
    this.bucket = Number.NaN;
    this.bucketEnd = Number.NaN;
    this.session = Number.NaN;
    this.brickOpen = Number.NaN;
    this.brickClose = Number.NaN;
  }

  /**
   * Feed one trade. `priceTicks` absolute integer ticks, `size` contracts, `aggressor` 1 buy /
   * 2 sell / 0 unknown, `tsMs` exchange time in integer epoch ms.
   */
  push(priceTicks: number, size: number, aggressor: number, tsMs: number): BarChange {
    const s = this.series;
    if (s.origin === null) s.origin = priceTicks;
    this.appended = 0;
    switch (this.spec.kind) {
      case "time":
      case "session":
        this.pushBucketed(priceTicks, size, aggressor, tsMs);
        break;
      case "tick":
        this.pushTick(priceTicks, size, aggressor, tsMs, this.spec.count);
        break;
      case "volume":
        this.pushVolume(priceTicks, size, aggressor, tsMs, this.spec.count);
        break;
      case "range":
        this.pushRange(priceTicks, size, aggressor, tsMs, this.spec.ticks);
        break;
      case "renko":
        this.pushRenko(priceTicks, size, aggressor, tsMs, this.spec.ticks);
        break;
    }
    this.lastAdded = this.appended;
    return this.lastAdded > 0 ? "new" : "updated";
  }

  /** Bars appended during the current push (a trim at the bar ceiling does not change it). */
  private appended = 0;

  // ------------------------------------------------------------------ bar primitives

  private bucketKey(tsMs: number): number {
    const spec = this.spec;
    if (spec.kind === "time") {
      const open = this.days.bucketOpen(tsMs, spec.periodMs); // = sessionBucket, no allocation
      this.keyEnd = this.days.bucketEnd;
      return open;
    }
    if (spec.kind === "session") return sessionKey(spec.unit, spec.count, this.days.of(tsMs));
    return Number.NaN;
  }

  private bucketOpen(key: number): number {
    const spec = this.spec;
    if (spec.kind === "time") return key;
    if (spec.kind === "session") return sessionOpenMs(spec.unit, spec.count, key);
    return Number.NaN;
  }

  /** Append a bar holding one trade (or an empty phantom when `size` < 0). Returns its index. */
  private open(o: number, h: number, l: number, c: number, openTs: number): number {
    const s = this.series;
    const origin = s.origin ?? 0;
    const i = s.appendSlot();
    this.appended++;
    s.open[i] = o - origin;
    s.high[i] = h - origin;
    s.low[i] = l - origin;
    s.close[i] = c - origin;
    s.volume[i] = 0;
    s.buyVolume[i] = 0;
    s.sellVolume[i] = 0;
    s.trades[i] = 0;
    s.openTs[i] = openTs;
    s.deltaHigh[i] = 0;
    s.deltaLow[i] = 0;
    return i;
  }

  /** Add a trade (or a split piece of one) to bar `i`: extremes, close, volume, count. */
  private add(i: number, price: number, size: number, aggressor: number): void {
    const s = this.series;
    const rel = price - (s.origin ?? 0);
    if (rel > (s.high[i] ?? rel)) s.high[i] = rel;
    if (rel < (s.low[i] ?? rel)) s.low[i] = rel;
    s.close[i] = rel;
    s.volume[i] = (s.volume[i] ?? 0) + size;
    if (aggressor === AGGRESSOR_BUY || aggressor === AGGRESSOR_SELL) {
      const before = (s.buyVolume[i] ?? 0) - (s.sellVolume[i] ?? 0);
      if (aggressor === AGGRESSOR_BUY) s.buyVolume[i] = (s.buyVolume[i] ?? 0) + size;
      else s.sellVolume[i] = (s.sellVolume[i] ?? 0) + size;
      // Running-delta extremes; a history bar without them (NaN) starts from what it is known to
      // have reached: its open (0) and its delta so far.
      const d = (s.buyVolume[i] ?? 0) - (s.sellVolume[i] ?? 0);
      const hi = s.deltaHigh[i] ?? Number.NaN;
      const lo = s.deltaLow[i] ?? Number.NaN;
      s.deltaHigh[i] = Math.max(Number.isNaN(hi) ? Math.max(0, before) : hi, d);
      s.deltaLow[i] = Math.min(Number.isNaN(lo) ? Math.min(0, before) : lo, d);
    }
    s.trades[i] = (s.trades[i] ?? 0) + 1;
  }

  /** Open time for a new trade-built bar: the trade's time, kept strictly after the last bar's. */
  private nextOpenTs(tsMs: number): number {
    const s = this.series;
    return s.length > 0 ? Math.max(tsMs, (s.openTs[s.length - 1] ?? 0) + 1) : tsMs;
  }

  /** Trade-built bars: true when this trade opens a new trading day (session reset, §12.3). */
  private newSession(tsMs: number): boolean {
    const day = this.days.of(tsMs);
    // A late print from an earlier day never re-opens that day.
    if (!(day > this.session) && this.series.length > 0) return false;
    this.session = day;
    this.brickOpen = Number.NaN;
    this.brickClose = Number.NaN;
    return true;
  }

  private abs(i: number, col: Int32Array): number {
    return (col[i] ?? 0) + (this.series.origin ?? 0);
  }

  // ------------------------------------------------------------------ bar types

  private pushBucketed(price: number, size: number, aggressor: number, tsMs: number): void {
    const s = this.series;
    const key = this.bucketKey(tsMs);
    // Late prints (key < bucket) fold into the current bar.
    if (s.length === 0 || key > this.bucket) {
      this.bucket = key;
      this.bucketEnd = this.keyEnd;
      this.open(price, price, price, price, this.bucketOpen(key));
    }
    this.add(s.length - 1, price, size, aggressor);
  }

  private pushTick(price: number, size: number, aggressor: number, tsMs: number, n: number): void {
    const s = this.series;
    const fresh = this.newSession(tsMs);
    if (fresh || (s.trades[s.length - 1] ?? 0) >= n)
      this.open(price, price, price, price, this.nextOpenTs(tsMs));
    this.add(s.length - 1, price, size, aggressor);
  }

  private pushVolume(price: number, size: number, aggressor: number, tsMs: number, n: number): void {
    const s = this.series;
    let fresh = this.newSession(tsMs);
    let left = size;
    do {
      const last = s.length - 1;
      if (fresh || (s.volume[last] ?? 0) >= n) {
        this.open(price, price, price, price, this.nextOpenTs(tsMs));
        fresh = false;
      }
      const i = s.length - 1;
      const take = Math.min(left, n - (s.volume[i] ?? 0));
      this.add(i, price, take, aggressor);
      left -= take;
    } while (left > 0);
  }

  private pushRange(price: number, size: number, aggressor: number, tsMs: number, r: number): void {
    const s = this.series;
    if (this.newSession(tsMs)) {
      this.open(price, price, price, price, this.nextOpenTs(tsMs));
      this.add(s.length - 1, price, size, aggressor);
      return;
    }
    const i = s.length - 1;
    const hi = this.abs(i, s.high);
    const lo = this.abs(i, s.low);
    if (Math.max(hi, price) - Math.min(lo, price) <= r) {
      this.add(i, price, size, aggressor);
      return;
    }
    const origin = s.origin ?? 0;
    const up = price > hi;
    // Close the bar at its boundary, then open one tick beyond with phantom bars across a gap.
    const boundary = up ? lo + r : hi - r;
    if (up) s.high[i] = boundary - origin;
    else s.low[i] = boundary - origin;
    s.close[i] = boundary - origin;
    let o = up ? boundary + 1 : boundary - 1;
    // Phantom bars off: a jump they would fill leaves the gap open; the next bar opens at the trade.
    if (this.spec.kind === "range" && this.spec.phantom === false && (up ? price > o + r : price < o - r))
      o = price;
    while (up ? price > o + r : price < o - r) {
      const far = up ? o + r : o - r;
      this.open(o, Math.max(o, far), Math.min(o, far), far, this.nextOpenTs(tsMs));
      o = up ? far + 1 : far - 1;
    }
    this.open(o, o, o, o, this.nextOpenTs(tsMs));
    this.add(s.length - 1, price, size, aggressor);
  }

  private pushRenko(price: number, size: number, aggressor: number, tsMs: number, b: number): void {
    const s = this.series;
    if (this.newSession(tsMs)) {
      const anchor = Math.floor((price + Math.floor(b / 2)) / b) * b;
      this.open(anchor, anchor, anchor, anchor, this.nextOpenTs(tsMs));
      this.add(s.length - 1, price, size, aggressor);
      return; // |price − anchor| ≤ B/2 < B: no brick yet
    }
    this.add(s.length - 1, price, size, aggressor);
    const origin = s.origin ?? 0;
    for (;;) {
      const i = s.length - 1;
      const formOpen = this.abs(i, s.open);
      let from: number;
      let to: number;
      if (Number.isNaN(this.brickClose)) {
        // No brick yet this session: B either way from the anchor.
        if (price >= formOpen + b) [from, to] = [formOpen, formOpen + b];
        else if (price <= formOpen - b) [from, to] = [formOpen, formOpen - b];
        else return;
      } else {
        const bo = this.brickOpen;
        const bc = this.brickClose;
        const upTrend = bc > bo;
        if (upTrend && price >= bc + b) [from, to] = [bc, bc + b];
        else if (upTrend && price <= bo - b) [from, to] = [bo, bo - b];
        else if (!upTrend && price <= bc - b) [from, to] = [bc, bc - b];
        else if (!upTrend && price >= bo + b) [from, to] = [bo, bo + b];
        else return;
      }
      // The forming bar becomes the brick (exact multiples, no wicks) …
      s.open[i] = from - origin;
      s.close[i] = to - origin;
      s.high[i] = Math.max(from, to) - origin;
      s.low[i] = Math.min(from, to) - origin;
      this.brickOpen = from;
      this.brickClose = to;
      // … and a new forming bar starts at the brick's close.
      this.open(to, Math.max(to, price), Math.min(to, price), price, this.nextOpenTs(tsMs));
    }
  }

  // ------------------------------------------------------------------ queries

  /** Epoch ms at which the current time/session bar closes; null for other types or no bars. */
  currentBarCloseMs(): number | null {
    const spec = this.spec;
    if (this.series.length === 0) return null;
    if (spec.kind === "time") return this.bucketEnd;
    if (spec.kind === "session") return sessionCloseMs(spec.unit, spec.count, this.bucket);
    return null;
  }

  /** Trades left before the current tick bar closes; null for other types or an empty series. */
  ticksRemaining(): number | null {
    if (this.spec.kind !== "tick" || this.series.length === 0) return null;
    return Math.max(0, this.spec.count - (this.series.trades[this.series.length - 1] ?? 0));
  }

  /** Contracts left before the current volume bar closes; null for other types or no bars. */
  volumeRemaining(): number | null {
    if (this.spec.kind !== "volume" || this.series.length === 0) return null;
    return Math.max(0, this.spec.count - (this.series.volume[this.series.length - 1] ?? 0));
  }

  // ------------------------------------------------------------------ history

  /**
   * Replace every bar with `bars` (history, time order, absolute ticks) and continue from the
   * last one. Returns the index shift for the view (change in length).
   */
  loadHistory(bars: readonly BarRecord[]): number {
    const shift = this.series.replaceHistory(bars, Number.POSITIVE_INFINITY);
    this.syncAfterPrepend();
    return shift;
  }

  /**
   * Call after `series.prepend` / `series.replaceHistory` / `loadHistory`: re-derive the live
   * state (time/session bucket, session of trade-built bars, Renko's last brick) from the series'
   * last bars, or forget it when the series became empty.
   */
  syncAfterPrepend(): void {
    const s = this.series;
    const n = s.length;
    this.bucket = Number.NaN;
    this.bucketEnd = Number.NaN;
    this.session = Number.NaN;
    this.brickOpen = Number.NaN;
    this.brickClose = Number.NaN;
    if (n === 0) return;
    const lastTs = s.openTs[n - 1] ?? 0;
    const spec = this.spec;
    if (spec.kind === "time" || spec.kind === "session") {
      this.bucket = this.bucketKey(lastTs);
      this.bucketEnd = this.keyEnd;
      return;
    }
    this.session = tradingDayOf(lastTs);
    if (spec.kind === "renko" && n >= 2 && tradingDayOf(s.openTs[n - 2] ?? 0) === this.session) {
      this.brickOpen = this.abs(n - 2, s.open);
      this.brickClose = this.abs(n - 2, s.close);
    }
  }
}

/** Build bars from a trade stream with a fresh builder (history replay, tests). */
export function buildBars(
  spec: BarSpec,
  trades: Iterable<readonly [price: number, size: number, aggressor: number, tsMs: number]>,
): BarRecord[] {
  const b = new BarBuilder(spec);
  for (const [p, sz, a, t] of trades) b.push(p, sz, a, t);
  return Array.from({ length: b.series.length }, (_, i) => b.series.bar(i));
}
